import React, { useMemo, useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { ButtonBasic } from "../../components/button/ButtonBasic";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import LoopVideoBlock from "../../components/video/LoopVideoBlock";

/**
 * 날짜와 섹션 번호 기반으로 비디오 URL 생성
 * @param {string} issueId - 이슈 ID (예: "2025-08")
 * @param {number} sectionIndex - 섹션 인덱스 (0부터 시작)
 * @returns {string|null} 비디오 URL
 */
const generateSectionVideoUrl = (issueId, sectionIndex) => {
	if (!issueId || typeof sectionIndex !== "number") return null;

	// issueId에서 연도와 월 추출 (예: "2025-08" → "2025-08")
	const dateMatch = issueId.match(/(\d{4}-\d{2})/);
	if (!dateMatch) return null;

	const datePrefix = dateMatch[1];
	const sectionNumber = sectionIndex + 1; // 1부터 시작하도록 변환

	const baseUrl =
		"https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fsections%2F";
	const fileName = `${datePrefix}-s${sectionNumber}.mp4`;
	const fullUrl = `${baseUrl}${fileName}?alt=media`;

	return fullUrl;
};

/**
 * SectionDescriptionCard
 * CoverStoryCard와 동일한 패딩값을 사용하는 섹션 설명 카드
 * @param {object} props
 * @param {string} props.title - 섹션 제목
 * @param {string} [props.description] - 섹션 설명
 * @param {boolean} [props.isMobile] - 모바일 여부
 * @param {string} [props.issueId] - 이슈 ID (비디오 URL 생성용)
 * @param {number} [props.sectionIndex] - 섹션 인덱스 (비디오 URL 생성용)
 * @param {Array} [props.sectionThreads] - 섹션 스레드 배열
 * @param {Record<string, any>} [props.userInfoById] - 유저 정보 객체
 * @param {Record<string, any>} [props.collectionInfoById] - 컬렉션 정보 객체
 * @param {object} [props.videoCacheInfo] - 비디오 캐시 정보 객체
 */
const SectionDescriptionCard = ({
	title,
	description,
	isMobile,
	issueId,
	sectionIndex,
	sectionThreads = [],
	userInfoById = {},
	collectionInfoById = {},
	videoCacheInfo,
}) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [currentVideoUrl, setCurrentVideoUrl] = useState(null);

	// 비디오 URL 생성 (fallback)
	const videoUrl = useMemo(() => {
		return generateSectionVideoUrl(issueId, sectionIndex);
	}, [issueId, sectionIndex]);

	// 캐시된 비디오 URL 사용
	useEffect(() => {
		if (videoCacheInfo?.cache?.isCached) {
			videoCacheInfo.cache.getCachedVideoUrl().then((cachedUrl) => {
				if (cachedUrl && cachedUrl !== videoCacheInfo.url) {
					setCurrentVideoUrl(cachedUrl);
					console.log(`✅ Using cached section ${sectionIndex + 1} video`);
				} else {
					setCurrentVideoUrl(videoCacheInfo.url);
					console.log(
						`📡 Using original section ${sectionIndex + 1} video URL`
					);
				}
			});
		} else if (videoCacheInfo?.url) {
			setCurrentVideoUrl(videoCacheInfo.url);
			// 개발 로그 과다 방지: 캐시 미적용 로그 제거
		} else {
			setCurrentVideoUrl(videoUrl);
			// 개발 로그 과다 방지
		}
	}, [videoCacheInfo, videoUrl, sectionIndex]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				pt: isMobile ? 3 : 5,
				overflow: "hidden",
				display: "flex",
				alignItems: "flex-start",
				flexDirection: "column",
			}}
		>
			{/* 텍스트 섹션 */}
			<Stack spacing={1} sx={{ minWidth: 0, zIndex: 9, flex: 1, alignItems: "flex-start" }}>
				<Typography
					variant={isMobile ? "h7" : "h5"}
					sx={{
						fontFamily:
							'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif',
						fontWeight: 900,
						color: theme.palette.text.primary,
						lineHeight: 1.4,
						overflow: "hidden",
						wordBreak: "keep-all",
						textOverflow: "ellipsis",
					}}
				>
					{title}
				</Typography>
				{description && !isMobile ? (
					<Typography
						variant="body1"
						sx={{
							color: theme.palette.text.primary,
							display: "-webkit-box",
							WebkitLineClamp: 3,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}
					>
						{description}
					</Typography>
				) : null}

				{/* 모두 보기 버튼 */}
				{issueId !== undefined && sectionIndex !== undefined && (
					<ButtonBasic
						variant="outlined"
						size={isMobile ? "sm" : "md"}
						radius="full"
						onClick={() =>
							navigate(`/monthly-threads/${issueId}/section/${sectionIndex}`)
						}
					>
						모두 보기
					</ButtonBasic>
				)}
			</Stack>
			{/* 비디오 섹션 - 컨테이너 하단에 고정 */}
			{currentVideoUrl && (
				<Box sx={{ minWidth: 0, mt: "auto" }}>
					<LoopVideoBlock
						videoUrl={currentVideoUrl}
						isMobile={isMobile}
						aspectRatio="1 / 1"
						enableHover={false}
						playOnHover={false}
						showError={false}
					/>
				</Box>
			)}
		</Box>
	);
};

export default SectionDescriptionCard;
